import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    LicenseDTO,
    downloadAvailableLicensesApi,
    useDeleteLicenseApi,
    useFilterLicensesApi,
    useSaveLicenseApi,
    useSearchLicenseApi,
} from '../../../api/license-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveLicenseModal } from './save-license-modal';
import SvgExcel from '../../../assets/icons/Excel';
import { UploadLicenseExcelModal } from './upload-license-excel-modal';
import SvgUpload from '../../../assets/icons/Upload';
import SvgDownload from '../../../assets/icons/Download';
import SvgChevronLeft from '../../../assets/icons/ChevronLeft';
import SvgChevronRight from '../../../assets/icons/ChevronRight';
import SvgFirstPage from '../../../assets/icons/FirstPage';
import SvgLastPage from '../../../assets/icons/LastPage';
import SvgDelete from '../../../assets/icons/Delete';
import { Formik, Form } from 'formik';
import { FormikTextField } from '../../../components/formik';
import SvgFilterList from '../../../assets/icons/FilterList';

export function LicenseApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<LicenseDTO | undefined>();
    const licenseInit = useSearchLicenseApi();
    const filterLicenses = useFilterLicensesApi();
    const saveLicense = useSaveLicenseApi();
    const deleteLicense = useDeleteLicenseApi();
    const [showUploadExcel, setShowUploadExcel] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    
    // Multi-select state
    const [selectedLicenses, setSelectedLicenses] = useState<number[]>([]);
    
    // Filter state
    const [filterBatchNumber, setFilterBatchNumber] = useState<string>('');

    const { result, error, busy, initialized, initializing } = licenseInit;
    const { result: filterResult, error: filterError, busy: filterBusy, initialized: filterInitialized, initializing: filterInitializing } = filterLicenses;

    const productTitle = useCallback(
        (id?: number) => {
            const product = result?.products?.find(x => x.id === id);
            return <span>{product ? `${product.name} ` : id}</span>;
        },
        [result?.products]
    );

    // Get deletable licenses (not connected to license-code)
    const deletableLicenses = useMemo(() => {
        return filterResult?.licenses?.filter(l => !l.licenseCode) || [];
    }, [filterResult?.licenses]);

    // Check if all deletable licenses are selected
    const allSelected = useMemo(() => {
        return deletableLicenses.length > 0 && 
               deletableLicenses.every(l => selectedLicenses.includes(l.serial!));
    }, [deletableLicenses, selectedLicenses]);

    // Toggle single selection
    const handleToggleSelect = useCallback((serial: number) => {
        setSelectedLicenses(prev => {
            if (prev.includes(serial)) {
                return prev.filter(s => s !== serial);
            } else {
                return [...prev, serial];
            }
        });
    }, []);

    // Toggle select all
    const handleToggleSelectAll = useCallback(() => {
        if (allSelected) {
            setSelectedLicenses([]);
        } else {
            setSelectedLicenses(deletableLicenses.map(l => l.serial!));
        }
    }, [allSelected, deletableLicenses]);

    // Toggle select batch
    const handleToggleSelectBatch = useCallback((batchNumber: string) => {
        const batchLicenses = deletableLicenses
            .filter(l => l.batchNumber === batchNumber)
            .map(l => l.serial!);

        const allBatchSelected = batchLicenses.every(serial => selectedLicenses.includes(serial));

        if (allBatchSelected) {
            // Deselect all licenses in this batch
            setSelectedLicenses(prev => prev.filter(s => !batchLicenses.includes(s)));
        } else {
            // Select all licenses in this batch
            setSelectedLicenses(prev => [...new Set([...prev, ...batchLicenses])]);
        }
    }, [deletableLicenses, selectedLicenses]);

    const columns: IBasicTableColumn<LicenseDTO>[] = useMemo(
        () => [
            {
                Header: (
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleToggleSelectAll}
                        disabled={deletableLicenses.length === 0}
                    />
                ),
                width: 40,
                Cell: ({ row }) => (
                    row.licenseCode ? <span></span> : (
                        <input
                            type="checkbox"
                            checked={selectedLicenses.includes(row.serial!)}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleToggleSelect(row.serial!);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )
                ),
            },
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 40,
                Cell: ({ row }) => (
                    <span className="d-flex align-items-center gap-1">
                        <N>{row.serial}</N>
                        {row.ticketStatus && (
                            <span 
                                className={`badge ${row.hasOpenTicket ? 'bg-warning text-dark' : 'bg-success'}`}
                                title={translate('ticket-status') + ': ' + translate(row.ticketStatus.toLowerCase())}
                                style={{ fontSize: '0.65rem', padding: '2px 4px' }}
                            >
                                🎫 {translate(row.ticketStatus.toLowerCase())}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                Header: <T>product-id</T>,
                name: 'productId',
                Cell: ({ row }) => productTitle(row.productId),
            },
            {
                Header: <T>license-key</T>,
                name: 'licenseKey',
            },
            {
                Header: <T>license-code</T>,
                name: 'licenseCode',
                width: 120,
            },
            {
                Header: <T>batch-number</T>,
                name: 'batchNumber',
                width: 150,
                Cell: ({ row }) => (
                    row.batchNumber ? (
                        <span
                            className="badge bg-info text-dark cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSelectBatch(row.batchNumber!);
                            }}
                            title="کلیک کنید تا همه لایسنس‌های این دسته انتخاب شوند"
                        >
                            {row.batchNumber}
                        </span>
                    ) : (
                        <span className="badge bg-secondary text-dark">-</span>
                    )
                ),
            },
            {
                Header: <T>is-delivered</T>,
                name: 'isDelivered',
                width: 120,
                Cell: ToggleCell,
            },
            {
                Header: <T>delivered-at</T>,
                name: 'deliveredAt',
                width: 120,
                Cell: DateCell,
            },
            {
                Header: <T>delivered-to-user</T>,
                name: 'deliveredToUserName',
                width: 150,
                Cell: ({ row }) => (
                    row.deliveredToUserName ? (
                        <span className="badge bg-success text-dark">
                            {row.deliveredToUserName}
                        </span>
                    ) : (
                        <span className="badge bg-secondary text-dark">-</span>
                    )
                ),
            },
        ],
        [productTitle, allSelected, handleToggleSelectAll, deletableLicenses, selectedLicenses, handleToggleSelect]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        licenseInit.post(null, {});
    }, [initialized, initializing, error, licenseInit]);

    // Load filtered licenses with debouncing
    useEffect(() => {
        const timer = setTimeout(() => {
            filterLicenses.post(null, {
                pageSize: pageSize,
                pageIndex: currentPage,
                batchNumber: filterBatchNumber || undefined
            });
        }, 300);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize, currentPage, filterBatchNumber]);

    // Update total count when filter result changes
    useEffect(() => {
        if (filterResult) {
            setTotalCount(filterResult.totalCount);
        }
    }, [filterResult]);

    // Clear selection when page changes
    useEffect(() => {
        setSelectedLicenses([]);
    }, [currentPage, pageSize]);

    const handleDelete = useCallback((license: LicenseDTO) => {
        bd.msgbox(
            translate('delete-license'),
            translate('confirm-delete-license'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteLicense.remove(
                            license,
                            () => {
                                // Refresh the filtered licenses
                                filterLicenses.post(null, {
                                    pageSize: pageSize,
                                    pageIndex: currentPage,
                                    batchNumber: filterBatchNumber || undefined
                                });
                                notify.dark(translate('selected-license-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedLicense(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    }, [deleteLicense, filterLicenses, pageSize, currentPage]);

    // Bulk delete handler
    const handleBulkDelete = useCallback(() => {
        if (selectedLicenses.length === 0) return;

        bd.msgbox(
            translate('delete-licenses'),
            `${translate('confirm-delete')} ${selectedLicenses.length} ${translate('licenses')}?`,
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        let completed = 0;
                        let failed = 0;

                        selectedLicenses.forEach((serial) => {
                            const license = filterResult?.licenses?.find(l => l.serial === serial);
                            if (license && !license.licenseCode) {
                                deleteLicense.remove(
                                    license,
                                    () => {
                                        completed++;
                                        if (completed + failed === selectedLicenses.length) {
                                            // Refresh the list
                                            filterLicenses.post(null, {
                                                pageSize: pageSize,
                                                pageIndex: currentPage,
                                                batchNumber: filterBatchNumber || undefined
                                            });
                                            setSelectedLicenses([]);
                                            notify.success(
                                                `${completed} ${translate('licenses-deleted-successfully')}`
                                            );
                                            hide();
                                        }
                                    },
                                    () => {
                                        failed++;
                                        if (completed + failed === selectedLicenses.length) {
                                            // Refresh the list
                                            filterLicenses.post(null, {
                                                pageSize: pageSize,
                                                pageIndex: currentPage,
                                                batchNumber: filterBatchNumber || undefined
                                            });
                                            setSelectedLicenses([]);
                                            if (completed > 0) {
                                                notify.warning(
                                                    `${completed} ${translate('deleted')}, ${failed} ${translate('failed')}`
                                                );
                                            } else {
                                                notify.error(translate('delete-failed'));
                                            }
                                            hide();
                                        }
                                    }
                                );
                            }
                        });
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    }, [selectedLicenses, filterResult, deleteLicense, filterLicenses, pageSize, currentPage]);

    const handleSave = useCallback((license: LicenseDTO) => {
        saveLicense.post(
            null,
            license,
            res => {
                // Refresh the filtered licenses
                filterLicenses.post(null, {
                    pageSize: pageSize,
                    pageIndex: currentPage,
                    batchNumber: filterBatchNumber || undefined
                });

                setShowModal(false);
                setSelectedLicense(undefined);
                notify.success(
                    selectedLicense
                        ? translate('selected-license-updated-successfully')
                        : translate('new-license-created-successfully')
                );
            },
            notify.error
        );
    }, [saveLicense, filterLicenses, pageSize, currentPage, selectedLicense, filterBatchNumber]);

    // Pagination handlers
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
    }, []);

    const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize]);

    const handleUploaded = useCallback((licenses: LicenseDTO[]) => {
        // Refresh the filtered licenses
        filterLicenses.post(null, {
            pageSize: pageSize,
            pageIndex: currentPage,
            batchNumber: filterBatchNumber || undefined
        });
        notify.success(translate('licenses-uploaded-successfully'));
        setShowUploadExcel(false);
    }, [filterLicenses, pageSize, currentPage, filterBatchNumber]);

    const handleDownloadExcel = useCallback(() => {
        setIsDownloading(true);
        downloadAvailableLicensesApi()
            .then(({ data, fileName }: any) => {
                const url = window.URL.createObjectURL(data);
                const a = document.createElement('a');
                a.href = url;
                a.target = '_blank';
                a.download = fileName ?? 'available-licenses.xlsx';
                document.body.appendChild(a);
                a.click();
                a.remove();
            })
            .finally(() => setIsDownloading(false));
    }, []);

    if (error || filterError) return <InitFailed ex={error || filterError} onRetry={() => {
        licenseInit.post(null, {});
        filterLicenses.post(null, { pageSize, pageIndex: currentPage, batchNumber: filterBatchNumber || undefined });
    }} />;
    if (!initialized || initializing || filterInitializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="licenses"
                    controls={
                        <bd.Flex align="center" gap={2}>
                            {selectedLicenses.length > 0 && (
                                <bd.Flex align="center" gap={2}>
                                    <span className="badge bg-primary">
                                        {selectedLicenses.length} <T>selected</T>
                                    </span>
                                    <LoadingButton
                                        variant="contained"
                                        color="secondary"
                                        size="sm"
                                        label={<T>delete-selected</T>}
                                        icon={<SvgDelete />}
                                        onClick={handleBulkDelete}
                                    />
                                </bd.Flex>
                            )}
                            <LoadingButton
                                variant="contained"
                                size="sm"
                                label={<T>new-license</T>}
                                icon={<SvgAdd />}
                                onClick={() => {
                                    setShowModal(true);
                                    setSelectedLicense(undefined);
                                }}
                                loading={busy}
                            />
                            <bd.Button
                                variant="outline"
                                className="text-warning border-warning"
                                size="sm"
                                onClick={() => setShowUploadExcel(true)}
                            >
                                <SvgUpload />
                                <SvgExcel />
                                <T>upload-excel-file</T>
                            </bd.Button>
                            <LoadingButton
                                className="text-success border-success"
                                variant="outline"
                                size="sm"
                                onClick={handleDownloadExcel}
                                icon={<SvgDownload />}
                                label={<T>download-available-licenses</T>}
                                loading={isDownloading}
                            />
                        </bd.Flex>
                    }
                />
                <bd.Paper className="h-100 p-3 mb-3">
                    <Formik
                        initialValues={{ batchNumber: '' }}
                        onSubmit={values => {
                            setFilterBatchNumber(values.batchNumber);
                            setCurrentPage(1);
                        }}
                    >
                        <Form>
                            <bd.Flex align="center" gap={2}>
                                <FormikTextField
                                    name="batchNumber"
                                    label={<T>batch-number</T>}
                                    placeholder="BATCH-20251102-235312"
                                    width="20rem"
                                />
                                <LoadingButton
                                    variant="contained"
                                    type="submit"
                                    className="mt-2 min-w-80"
                                    icon={<SvgFilterList />}
                                    label={<T>filter</T>}
                                    loading={filterBusy}
                                />
                            </bd.Flex>
                        </Form>
                    </Formik>
                </bd.Paper>
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        hover
                        columns={columns}
                        data={filterResult?.licenses || []}
                        onRowClick={({ row }) => {
                            setSelectedLicense(row);
                            setShowModal(true);
                        }}
                        rowClassName={({ row }: { row: LicenseDTO }) => 
                            row.hasOpenTicket ? 'table-warning' : ''
                        }
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    {/* Pagination Controls */}
                    {totalCount > 0 && (
                        <bd.Flex justify="between" align="center" className="p-3 border-top">
                            <bd.Flex align="center" gap={2}>
                                <T>showing-{((currentPage - 1) * pageSize) + 1}-to-{Math.min(currentPage * pageSize, totalCount)}-of-{totalCount}</T>
                                <select
                                    className="form-select"
                                    value={pageSize}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handlePageSizeChange(Number(e.target.value))}
                                    style={{ width: '80px' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <T>per-page</T>
                            </bd.Flex>
                            
                            <bd.Flex align="center" gap={1}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                    title={<T>first-page</T>}
                                >
                                    <SvgFirstPage className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    title={<T>previous-page</T>}
                                >
                                    <SvgChevronLeft className="rtl-rotate-180" />
                                </bd.Button>
                                <span className="px-2">
                                    <T>page-{currentPage}-of-{totalPages}</T>
                                </span>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    title={<T>next-page</T>}
                                >
                                    <SvgChevronRight className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                    title={<T>last-page</T>}
                                >
                                    <SvgLastPage className="rtl-rotate-180" />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    )}

                    <SaveLicenseModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedLicense(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onKeyUpdated={(updatedLicense) => {
                            // بروزرسانی لیست بعد از تغییر کلید لایسنس
                            filterLicenses.post(null, {
                                pageSize: pageSize,
                                pageIndex: currentPage,
                                batchNumber: filterBatchNumber || undefined
                            });
                            setSelectedLicense(updatedLicense);
                        }}
                        selectedLicense={selectedLicense}
                        products={result?.products || []}
                        allowEdit={
                            selectedLicense ? !selectedLicense.isDelivered : result?.allowEdit || false
                        }
                    />
                    {showUploadExcel && (
                        <UploadLicenseExcelModal
                            show={showUploadExcel}
                            onHide={() => setShowUploadExcel(false)}
                            onUploaded={handleUploaded}
                        />
                    )}
                </bd.Paper>
            </div>
        </TProvider>
    );
}
